import { useEffect, useRef, useState } from 'react';
// Legacy-сборка: работает и в браузерах без новейших возможностей JavaScript.
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

/**
 * Просмотр PDF средствами pdf.js: страницы рисуются на странице приложения, поэтому
 * одинаково работают в любом браузере, в том числе на телефоне (Android и iOS не
 * показывают PDF во фрейме). Текст страниц выделяется и копируется — поверх картинки
 * лежит слой с текстом. Библиотека загружается только при открытии просмотра.
 */
export function PdfView({ url }: { url: string }) {
  const container = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; pages: number; error?: string }>({ status: 'loading', pages: 0 });

  useEffect(() => {
    let cancelled = false;
    let destroy: (() => void) | undefined;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Не удалось загрузить файл');
        const data = new Uint8Array(await res.arrayBuffer());
        const task = pdfjs.getDocument({ data });
        destroy = () => void task.destroy();
        const doc = await task.promise;
        if (cancelled) return;
        setState({ status: 'ready', pages: doc.numPages });
        const root = container.current!;
        root.replaceChildren();
        const width = Math.min(root.clientWidth - 16, 1000);
        const ratio = window.devicePixelRatio || 1;
        for (let n = 1; n <= doc.numPages && !cancelled; n++) {
          const page = await doc.getPage(n);
          const scale = width / page.getViewport({ scale: 1 }).width;
          // Экранный размер — для слоя текста, в ratio раз крупнее — для чёткой картинки.
          const css = page.getViewport({ scale });
          const viewport = page.getViewport({ scale: scale * ratio });
          const box = document.createElement('div');
          box.className = 'pdf-page';
          box.style.width = `${Math.floor(css.width)}px`;
          box.style.height = `${Math.floor(css.height)}px`;
          box.style.setProperty('--total-scale-factor', String(scale));
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.setAttribute('aria-label', `Страница ${n}`);
          // Поверх картинки — прозрачный текст на тех же местах: его можно выделить, скопировать и найти (Ctrl+F).
          // У скана текста нет — там слой пустой.
          const text = document.createElement('div');
          text.className = 'textLayer';
          text.addEventListener('pointerdown', () => text.classList.add('selecting'));
          box.append(canvas, text);
          root.appendChild(box);
          await page.render({ canvas, viewport }).promise;
          await new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: text, viewport: css }).render();
        }
      } catch (e) {
        if (!cancelled) setState({ status: 'error', pages: 0, error: (e as Error).message });
      }
    })();
    // Выделение закончилось — слои текста возвращаются в обычный режим.
    const release = () => container.current?.querySelectorAll('.textLayer.selecting').forEach((el) => el.classList.remove('selecting'));
    document.addEventListener('pointerup', release);
    return () => {
      cancelled = true;
      destroy?.();
      document.removeEventListener('pointerup', release);
    };
  }, [url]);

  return (
    <div className="pdf-view">
      {state.status === 'loading' && (
        <div className="empty">
          <span className="spinner" aria-hidden />
          Открываем…
        </div>
      )}
      {state.status === 'error' && <div className="empty">Не удалось показать файл: {state.error}. Скачайте его.</div>}
      <div ref={container} className="pdf-pages" />
    </div>
  );
}

export function TextView({ url }: { url: string }) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(url, { credentials: 'same-origin' })
      .then((r) => r.text())
      .then((t) => !cancelled && setText(t))
      .catch(() => !cancelled && setText('Не удалось загрузить файл'));
    return () => {
      cancelled = true;
    };
  }, [url]);
  return <pre className="text-view">{text ?? 'Открываем…'}</pre>;
}
